import { Card, Text, Flex, Heading, Badge, Button, Box } from '@chakra-ui/react';
import { ConfirmModal, createDataCollectionContext, dbTimeToUserTime, toaster, useDrawer, useMobileContext, type ConfirmModalHandle } from '@shared';
import { useQueryClient } from '@tanstack/react-query';
import { createColumnHelper, type Table } from '@tanstack/react-table';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import accountsService from '../../api/services/accountsService';
import { useDeleteMultipleAccounts } from '../../api/hooks/useAccountSuperadmin';
import { IoEnterOutline } from 'react-icons/io5';
import { SuperadminAuthService } from '../../api/services/superadminAuthService';
import AccountDrawer from './SuperadminAccountsDrawer';
import ModifyAccountCreditsModal from './ModifyAccountCreditsModal';
import { countries } from '@shared';
import { useMeta } from '../../api/hooks/useMeta';

const columnHelper = createColumnHelper<Account>();
const TIMEZONE = import.meta.env.VITE_DEFAULT_TIMEZONE as string;
const APP_URL = import.meta.env.VITE_APP_URL as string;

export default function Accounts() {
	const { DataCollectionContextProvider, DataCollectionTable, DataCollectionTableFooter, DataCollectionTableHeader } = useMemo(() => createDataCollectionContext<Account>(), []);
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const { isMobile } = useMobileContext();
	const { openDrawer } = useDrawer();
	const { meta } = useMeta('accountsService');

	function getAccountStatusTranslation(status: AccountStatus): string {
		switch (status) {
			case 'waitingForApproval':
				return t('general.waiting_for_approval');
			case 'rejected':
				return t('general.rejected');
			case 'approved':
				return t('general.approved');
			case 'locked':
				return t('general.locked');
			default:
				return t('general.unknown');
		}
	}

	function getBadgeColorByStatus(status: AccountStatus): string {
		switch (status) {
			case 'waitingForApproval':
				return 'yellow';
			case 'approved':
				return 'green';
			case 'locked':
				return 'red';
			case 'rejected':
				return 'red';
			default:
				return 'yellow';
		}
	}

	async function handleLoginToAccount(accountID: number) {
		const response = await SuperadminAuthService.loginAccount(accountID);
		window.open(`${APP_URL}/?jwt=${response.jwt}`, '_blank');
	}

	async function handleCreditAddRemove(account: Account): Promise<void> {
		await confirmRef.current?.open({
			title: t('general.modify_credits'),
			content: <ModifyAccountCreditsModal credits={account.creditCount} accountID={account.accountID} ref={confirmRef} />,
			isConfirmButtonVisible: false,
			isBackButtonVisible: false,
		});
	}

	const columns = [
		columnHelper.display({
			id: 'buttons',
			cell: ({ row }) => {
				return (
					<Flex gap={2}>
						<Button
							size="xs"
							paddingRight={3}
							paddingLeft={4}
							onClick={e => {
								e.stopPropagation();
								handleLoginToAccount(row.original.accountID);
							}}
						>
							{t('general.login_to_account')}
							<IoEnterOutline
								style={{
									height: '22px',
									width: '22px',
								}}
							/>
						</Button>
						<Button
							size="xs"
							onClick={e => {
								e.stopPropagation();
								handleCreditAddRemove(row.original);
							}}
						>
							{t('general.add_or_remove_credits')}
						</Button>
					</Flex>
				);
			},
		}),
		columnHelper.accessor('accountID', {
			header: t('general.id'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('name', {
			header: t('general.name'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('accountStatus', {
			header: t('general.status'),
			sortingFn: 'basic',
			filterFn: 'arrIncludesSomeSensitive',
			meta: {
				filterVariant: 'select',
				filterOptions: [
					{ value: 'waitingForApproval', label: meta?.accountStatus?.['waitingForApproval'] ?? '' },
					{ value: 'approved', label: meta?.accountStatus?.['approved'] ?? '' },
					{ value: 'rejected', label: meta?.accountStatus?.['rejected'] ?? '' },
					{ value: 'locked', label: meta?.accountStatus?.['locked'] ?? '' },
				] as Array<{ value: AccountStatus; label: string }>,
			},
			cell: ({ row }) => {
				return (
					<Badge size="md" colorPalette={getBadgeColorByStatus(row.original.accountStatus)}>
						{getAccountStatusTranslation(row.original.accountStatus)}
					</Badge>
				);
			},
		}),
		columnHelper.accessor('declineReason', {
			id: 'declineReason',
			header: t('general.decline_reason'),
			enableSorting: false,
			size: 20,
			maxSize: 200,
		}),
		columnHelper.accessor('creditCount', {
			header: t('general.credits'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('businessID', {
			header: t('general.business_id'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('isInvoicingEnabled', {
			header: t('general.is_invoicing_enabled'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (row.original.isInvoicingEnabled) {
					return t('general.yes');
				}
				return t('general.false');
			},
		}),
		columnHelper.accessor('invoiceContactName', {
			header: t('general.invoice_contact_name'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('invoiceDeliveryMethod', {
			header: t('general.invoice_delivery_method'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('vatCode', {
			header: t('general.vat_code'),
			sortingFn: 'basic',
		}),

		columnHelper.accessor('postalAddress', {
			header: t('general.postal_address'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('postalCode', {
			header: t('general.postal_code'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('city', {
			header: t('general.city'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('countryCode', {
			header: t('general.country'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				const country = countries.find(c => c.code === row.original.countryCode.toUpperCase())?.name || t('general.unknown');
				return <Text>{country}</Text>;
			},
		}),
		columnHelper.accessor('timeLatestLogin', {
			header: t('general.time_latest_login'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (!row.original.timeLatestLogin) {
					return null;
				}
				return dbTimeToUserTime(row.original.timeLatestLogin, TIMEZONE);
			},
		}),
		columnHelper.accessor('timeLatestMessageSent', {
			header: t('general.time_latest_message_sent'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (!row.original.timeLatestMessageSent) {
					return null;
				}
				return dbTimeToUserTime(row.original.timeLatestMessageSent, TIMEZONE);
			},
		}),
		columnHelper.accessor('timeCreditsExpire', {
			header: t('general.time_credits_expire'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (!row.original.timeCreditsExpire) {
					return null;
				}
				return dbTimeToUserTime(row.original.timeCreditsExpire, TIMEZONE);
			},
		}),
		columnHelper.accessor('timeCreated', {
			header: t('general.time_created'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return <Text>{dbTimeToUserTime(row.original.timeCreated, TIMEZONE)}</Text>;
			},
		}),
		columnHelper.accessor('timeModified', {
			header: t('general.time_modified'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (!row.original.timeModified) {
					return null;
				}
				return <Text>{dbTimeToUserTime(row.original.timeModified, TIMEZONE)}</Text>;
			},
		}),
	];

	const confirmRef = useRef<ConfirmModalHandle>(null);
	const { mutate: deleteMultipleAccounts } = useDeleteMultipleAccounts();

	const selectedItemsActions = [
		{
			id: 'deleteMany',
			label: t('general.delete_selected'),
			callbackFn: async (ids: Array<string>, table: Table<Account>) => {
				const payload = ids.map(id => {
					return {
						accountID: parseInt(id),
						delete: true,
					};
				});

				const response = await confirmRef.current?.open({
					title: t('general.remove_accounts'),
					content: t('general.really_remove_accounts'),
					confirmButtonText: t('general.remove'),
					confirmButtonVariant: 'subtle',
				});
				if (response === true) {
					deleteMultipleAccounts(payload, {
						onSuccess: () => {
							toaster.create({
								type: 'success',
								title: t('general.removed'),
							});
							queryClient.invalidateQueries({ queryKey: ['accounts'] });
							table.resetRowSelection();
						},
					});
				}
			},
		},
	];

	return (
		<Flex flexDirection={'column'} gap={5} width={'100%'} alignItems={'center'} height="100%">
			{!isMobile ? (
				<Card.Root maxWidth={'2000px'} width={'100%'}>
					<Card.Body>
						<Heading>{t('general.accounts')}</Heading>
						<Text>{t('general.accounts_guide')}</Text>
					</Card.Body>
				</Card.Root>
			) : null}
			<Flex overflow="auto" width={'100%'} display={'flex'} flexDirection={'column'} height={'100%'} maxW={'2000px'}>
				<Flex display={'flex'} flex={1} overflow={'auto'}>
					<Card.Root flex={1} display="flex">
						<Card.Body display="flex" flexDirection="column" padding={5} gap={5} overflow={isMobile ? 'initial' : 'auto'}>
							<DataCollectionContextProvider
								dataKey="accounts"
								keyAttribute="accountID"
								emptyDataText={t('general.no_accounts_found')}
								fetchFn={(...args) => accountsService.getAllResources(...args)}
								queryParams={{ scopeWith: 'timeLatestLogin|creditCount|timeLatestMessageSent|timeCreditsExpire' }}
								selectedItemsActions={selectedItemsActions}
								initialItemsPerPage={25}
								columns={columns}
								defaultHiddenColumns={['vatCode', 'postalAddress', 'postalCode', 'city', 'declineReason']}
								isServerSide={true}
								isSelectable={false}
								defaultSort={{ id: 'timeLatestLogin', desc: true }}
								onClickRow={row => {
									openDrawer({
										id: 'accountDrawer',
										content: <AccountDrawer accountID={row.original.accountID} />,
									});
								}}
							>
								<Flex gap={2} flexWrap="wrap" flexShrink={0} ml="auto">
									<DataCollectionTableHeader />
								</Flex>
								<Box flex={1} overflow="auto" minH={0}>
									<DataCollectionTable />
								</Box>
								<Flex gap={2} flexShrink={0}>
									<DataCollectionTableFooter />
									<ConfirmModal ref={confirmRef} />
								</Flex>
							</DataCollectionContextProvider>
						</Card.Body>
					</Card.Root>
				</Flex>
			</Flex>
		</Flex>
	);
}
